import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../services/supabase";
import { useCustomFieldDefinitions } from "./useCustomFieldDefinitions";

/**
 * A single curriculum custom field, ready to render on a profile screen.
 * `label` is the tenant-defined display name (e.g. "Program"), `value` is the
 * student's curriculum value for that field ("" when the admin hasn't set it).
 */
export interface CurriculumCustomField {
  key: string;
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// useCurriculumCustomFields
// ---------------------------------------------------------------------------

/**
 * Resolve the custom fields for a specific curriculum.
 *
 * Given a `curriculumId`, this hook:
 *   1. Reads the field definitions from `org_custom_fields` (the current system,
 *      field_number → label); falls back to the legacy `organizations
 *      .custom_fields` JSONB for tenants that haven't migrated.
 *   2. Reads the per-field values from `curriculums.custom_fields` for that
 *      curriculum row (keyed `custom_${field_number}` in the current system).
 *   3. Merges them into `{key, label, value}` triples ready for display.
 *
 * This mirrors how SetupCurriculum / CurriculumDialog read + write the fields,
 * so profile screens stay consistent with the admin setup screens.
 *
 * Returns an empty list immediately when `curriculumId` is null.
 */
export function useCurriculumCustomFields(curriculumId: string | null): {
  fields: CurriculumCustomField[];
  curriculumName: string | null;
  loading: boolean;
} {
  const { orgId } = useAuth();
  // Field definitions: prefer the current system (`org_custom_fields`,
  // field_number → label). The enrolment "Curriculum details" panel rendered
  // every field blank because this hook only read the LEGACY
  // `organizations.custom_fields` JSONB (semantic keys like "Program"), whereas
  // the curriculum stores its values under `custom_1..N` keys (LMS Sprint
  // 2026-06). We keep the legacy JSONB as a fallback for un-migrated tenants.
  const { definitions, loading: defsLoading } = useCustomFieldDefinitions("curriculum");
  const [legacyDefs, setLegacyDefs] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [curriculumName, setCurriculumName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    if (!curriculumId) {
      setValues({});
      setLegacyDefs({});
      setCurriculumName(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        // Legacy definitions (key -> label) still live on the org row; only
        // used when the tenant has no `org_custom_fields` definitions.
        const orgPromise = orgId
          ? supabase
              .from("organizations")
              .select("custom_fields")
              .eq("id", orgId)
              .maybeSingle()
          : Promise.resolve({ data: null });

        // The curriculum carries the per-field values under its `custom_fields`
        // JSONB column — keyed `custom_${field_number}` in the current system,
        // or by the legacy semantic keys for un-migrated tenants. Also pull
        // `curriculum_name` so profile screens can render the Curriculum field
        // from the (policy-correct) enrolment curriculum.
        const curriculumPromise = supabase
          .from("curriculums")
          .select("custom_fields, curriculum_name")
          .eq("id", curriculumId)
          .maybeSingle();

        const [{ data: org }, { data: curriculum }] = await Promise.all([
          orgPromise,
          curriculumPromise,
        ]);

        if (cancelled) return;
        // `custom_fields` is a JSONB column (typed as Json); both reads are
        // string-keyed maps in practice, so narrow through `unknown`.
        setLegacyDefs(
          ((org?.custom_fields ?? {}) as unknown) as Record<string, string>,
        );
        setValues(
          ((curriculum?.custom_fields ?? {}) as unknown) as Record<string, unknown>,
        );
        setCurriculumName(
          (curriculum as { curriculum_name?: string | null } | null)?.curriculum_name ?? null,
        );
      } catch {
        if (!cancelled) {
          setLegacyDefs({});
          setValues({});
          setCurriculumName(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [curriculumId, orgId]);

  const fields = useMemo<CurriculumCustomField[]>(() => {
    if (!curriculumId) return [];

    // Preferred: current-system definitions from `org_custom_fields`. Values are
    // keyed `custom_${field_number}` on the curriculum. De-dupe by field_number
    // so stray duplicate definitions don't render the same field twice.
    if (definitions && definitions.length > 0) {
      const seen = new Set<number>();
      return definitions
        .filter((d) => {
          if (seen.has(d.field_number)) return false;
          seen.add(d.field_number);
          return true;
        })
        .map((d) => {
          const key = `custom_${d.field_number}`;
          const raw = values[key];
          return { key, label: d.field_label, value: raw == null ? "" : String(raw) };
        });
    }

    // Legacy fallback: `organizations.custom_fields` is overloaded — alongside
    // the field definitions (key -> string label) it can hold non-field keys
    // whose value is an object (e.g. `branding`). Only string-labelled entries
    // are real definitions; the curriculum stores values under the same keys.
    return Object.entries(legacyDefs)
      .filter(([, label]) => typeof label === "string")
      .map(([key, label]) => {
        const raw = values[key];
        return { key, label: String(label), value: raw == null ? "" : String(raw) };
      });
  }, [curriculumId, definitions, legacyDefs, values]);

  // When there is no curriculum to resolve there is nothing to show — report
  // not-loading immediately and ignore the org-definitions prefetch state
  // (`defsLoading`), which would otherwise keep this `true` on first render.
  return { fields, curriculumName, loading: curriculumId ? loading || defsLoading : false };
}

// ---------------------------------------------------------------------------
// useStudentCurriculumCustomFields (thin wrapper — preserved for callers)
// ---------------------------------------------------------------------------

/**
 * Resolve a student's curriculum custom fields for display.
 *
 * "Program" (and any other tenant attribute the admin wants on the profile) is
 * NOT a column on the student or class — it's a *curriculum custom field*. The
 * SET of fields is defined once on the organization
 * (`organizations.custom_fields`, key → display label) and each curriculum
 * stores its values under those same keys (`curriculums.custom_fields[key]`).
 * This mirrors how SetupCurriculum / CurriculumDialog read + write the fields,
 * so the profile stays consistent with the admin setup screens.
 *
 * The student's curriculum is reached through the FK that actually exists:
 *   student_grade_levels → school_years → curriculums
 * (school_years owns `curriculum_id`; grade_levels does not). There is no
 * `class_courses` junction — the previous program-derivation hook embedded one
 * and 400'd on every profile (PGRST200), which is why this replaced it.
 *
 * Delegates to `useCurriculumCustomFields` once the active-enrolment
 * curriculum_id is resolved.
 */
export function useStudentCurriculumCustomFields(studentId: string | null): {
  fields: CurriculumCustomField[];
  curriculumName: string | null;
  loading: boolean;
} {
  const [curriculumId, setCurriculumId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<boolean>(true);

  // Step 1: resolve the student's active-enrolment curriculum_id.
  useEffect(() => {
    let cancelled = false;

    if (!studentId) {
      setCurriculumId(null);
      setResolving(false);
      return;
    }

    setResolving(true);

    (async () => {
      try {
        // Curriculum is now an enrolment fact (student_grade_levels
        // .curriculum_id); fall back to the school year's curriculum for
        // legacy enrolments that predate the column.
        const { data: sgl } = await supabase
          .from("student_grade_levels")
          .select("active, updated_at, curriculum_id, school_years(curriculum_id)")
          .eq("student_id", studentId)
          .order("active", { ascending: false })
          .order("updated_at", { ascending: false })
          .limit(1);

        if (cancelled) return;

        // The select references `student_grade_levels.curriculum_id`, a column
        // that exists at runtime (curriculum-on-enrolment migration) but is not
        // yet in the generated database.types — so PostgREST infers a
        // SelectQueryError here. Read the row through a narrow local shape
        // rather than `any`.
        type SglCurriculumRow = {
          curriculum_id?: string | null;
          school_years?: { curriculum_id?: string | null } | { curriculum_id?: string | null }[] | null;
        };
        const sgl0 = (sgl ?? [])[0] as unknown as SglCurriculumRow | undefined;
        const schoolYear = Array.isArray(sgl0?.school_years)
          ? sgl0?.school_years[0]
          : sgl0?.school_years;
        const resolved: string | null =
          sgl0?.curriculum_id ?? schoolYear?.curriculum_id ?? null;

        setCurriculumId(resolved);
      } catch {
        if (!cancelled) setCurriculumId(null);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // Step 2: delegate field resolution to the extracted hook.
  const { fields, curriculumName, loading: fieldsLoading } = useCurriculumCustomFields(
    resolving ? null : curriculumId,
  );

  return { fields, curriculumName, loading: resolving || fieldsLoading };
}
